import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { BaseDialog } from "src/components/BaseDialog/BaseDialog";
import React, { useEffect, useState } from "react";
import { ChevronUpIcon } from "src/icons/ChevronUpIcon";
import { ChevronDownIcon } from "src/icons/ChevronDownIcon";
import { InformationIcon } from "src/icons/planner/InformationIcon";
import { ArrowLeftRightIcon } from "src/icons/planner/ArrowLeftRightIcon";
import { useGetExchangeRatesQuery } from "src/api/v1/quote-ApiSlice";
import { ManufacturerAutocomplete } from "src/pages/OfferPage/TableSvgOrganizer/ManufacturerAutocomplete";
import {
    AddUserCustomItemRequest,
    UpdateUserCustomItemRequest,
    useAddUserCustomItemMutation,
    useUpdateMyProductMutation
} from "src/api/v1/user-custom-items-ApiSlice";
import { useGetMyProductQuery } from "src/api/v2/user-custom-items-v2-ApiSlice";
import { customDevChanged } from "src/components/Planner/planner/planner";
import { IconPicker } from "src/pages/PlannerPage/DeviceView/UserCustomItem/IconPicker";
import { IconWithLiveColor } from "src/pages/PlannerPage/DeviceView/UserCustomItem/IconWithLiveColorChange";
import { ManufacturerRecommendationForUser } from "src/model/manufacturer-recommendation-project";
import { ts } from "src/utils/translate";
import { theme } from "src/theme/mui-theme";
import { DeleteUserCustomItemDialog } from "src/pages/PlannerPage/DeviceView/UserCustomItem/DeleteUserCustomItemDialog";
import { grey } from "@mui/material/colors";
import { useAppDispatch } from "src/app/hooks";
import { showSnackBar } from "src/api/ui-slice";

interface DeviceViewAddNewProductModalProps {
    onClose: (newItemId?: number) => void;
    productId?: number | null;
}

type Icon = { id: number; icon_svg: string; color: string };

export const AddOrEditUserCustomItemDialog = ({ onClose, productId }: DeviceViewAddNewProductModalProps) => {

    const dispatch = useAppDispatch();
    const { data: exchangeRates } = useGetExchangeRatesQuery();
    const [ isDeleteOpen, setIsDeleteOpen ] = useState(false);
    const [ selectedProductId, setSelectedProductId ] = useState<any>();
    const [ selectedProductName, setSelectedProductName ] = useState<any>();

    const [preSelectedId, setPreSelectedId] = useState<number | null>();
    const [preSelectedType, setPreSelectedType] = useState<string | null>();

    const [ priceOpen, setPriceOpen ] = useState(false);
    const [selectedManufacturer, setSelectedManufacturer] = useState<ManufacturerRecommendationForUser | null>(null);
    const [icon, setIcon] = useState<Icon | undefined>(undefined);
    const [iconColor, setIconColor] = useState("#000000");

    const [addUserCustomItem] = useAddUserCustomItemMutation();
    const { data: existingProduct } = useGetMyProductQuery(
        { productId: productId! },
        {
            skip: !productId,
        }
    );
    const [updateMyProduct] = useUpdateMyProductMutation();

    const hasProduct = !!productId && !!existingProduct?.product;

    const [formData, setFormData] = useState<AddUserCustomItemRequest>({
        product_name: "",
        article_number: "",
        icon_id: 0,
        icon_color: "",
        price_huf: 0,
        price_eur: 0,
        price_gbp: 0,
        price_czk: 0,
        price_usd: 0,
        price_pln: 0,
        price_chf: 0,
        quote_image_url: "",
        details: "",
        internal_product_manufacturer_id: null,
        user_manufacturer_id: null,
        is_auto_exchange: 0,
        comment: ""
    });

    const [prices, setPrices] = useState<{ [currency: string]: number }>({
        USD: 0,
        EUR: 0,
        GBP: 0,
        HUF: 0,
        CZK: 0,
        PLN: 0,
        CHF: 0,
    });

    const handleClose = (newItemId?: number) => {
        onClose(newItemId);
    };

    function togglePrice() {
        setPriceOpen(!priceOpen);
    }


    const handlePriceChange = (currency: string, value: number) => {
        const num = value;

        if (!exchangeRates || isNaN(num)) return;

        const newPrices: { [currency: string]: number } = { ...prices, [currency]: num };

        if (formData.is_auto_exchange === 1) {
            Object.keys(prices).forEach(targetCurrency => {
                if (targetCurrency !== currency) {
                    const rate = (exchangeRates as any)[currency.toLowerCase()]?.[targetCurrency.toLowerCase()];
                    if (rate !== undefined) {
                        newPrices[targetCurrency] = parseFloat((num * rate).toFixed(2));
                    }
                }
            });
        }

        setPrices(newPrices);

        setFormData(prev => ({
            ...prev,
            price_usd: newPrices.USD,
            price_eur: newPrices.EUR,
            price_gbp: newPrices.GBP,
            price_huf: newPrices.HUF,
            price_czk: newPrices.CZK,
            price_pln: newPrices.PLN,
            price_chf: newPrices.CHF,
        }));
    };

    const updateFormData = (key: keyof typeof formData, value: any) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const buildUpdateRequest = (
        formData: AddUserCustomItemRequest,
        productId: number
    ): UpdateUserCustomItemRequest => {
        return {
          user_custom_item_id: productId,
          ...formData,
        };
    };

    const handleSubmit = async () => {

        const missingFields: string[] = [];

        if (!formData.product_name) missingFields.push("product_name");
        if (!formData.icon_id) missingFields.push("icon_id");

        const currencies = ["price_huf","price_eur","price_gbp","price_czk","price_usd","price_pln","price_chf"];
        currencies.forEach((c) => {
            if (formData[c as keyof typeof formData] === null || formData[c as keyof typeof formData] === undefined) {
                missingFields.push(c);
            }
        });

        if (!formData.internal_product_manufacturer_id && !formData.user_manufacturer_id) {
            missingFields.push("internal_product_manufacturer_id OR user_manufacturer_id");
            return;
        }

        if (missingFields.length > 0) {
            console.error("Hiányzó kötelező mezők:", missingFields.join(", "));
            return;
        }

        if (hasProduct) {
            if (!productId) return;
            const updateRequest = buildUpdateRequest(formData, productId);
            try {
                await updateMyProduct(updateRequest).unwrap();
                customDevChanged("user_custom_item_" + productId, icon?.icon_svg ?? "", formData.icon_color);
                handleClose();
            } catch (err) {
                dispatch(showSnackBar({
                    text: ts('update_custom_item_error', 'Failed to update product'),
                    severity: 'error',
                }));
            }
        } else {
            try {
                await addUserCustomItem(formData).then((response) => { handleClose(response.data?.id) });
            } catch (err) {
                console.error("Hiba történt:", err);
            }
        }
    };

    const handleClone = async () => {
        if (!hasProduct) return;

        try {
            await addUserCustomItem(formData).then((response) => { handleClose(response.data?.id) });
        } catch (err) {
            console.error("Hiba történt a klonozasnal:", err);
        }
    }

    useEffect(() => {
        if (hasProduct && existingProduct?.product?.userCustomItem) {
            const customItem = existingProduct.product.userCustomItem;
            if (!customItem.is_auto_exchange) {
                setPriceOpen(true);
            } else {
                setPriceOpen(false);
            }
        }
    }, [hasProduct, existingProduct]);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            icon_id: icon?.id ?? 0,
            icon_color: iconColor ?? "#000000",
            internal_product_manufacturer_id: selectedManufacturer?.internal_product_manufacturer_id ?? null,
            user_manufacturer_id: selectedManufacturer?.user_manufacturer_id ?? null
        }));
    }, [icon, iconColor, selectedManufacturer]);

    useEffect(() => {
        if (existingProduct?.product && productId) {
            const p = existingProduct.product;
            const customItem = p.userCustomItem;
            setFormData({
                product_name: customItem.product_name || "",
                article_number: customItem.article_number || "",
                icon_id: customItem.icon_id || 0,
                icon_color: p.icon_color || "",
                price_huf: customItem.price_huf || 0,
                price_eur: customItem.price_eur || 0,
                price_gbp: customItem.price_gbp || 0,
                price_czk: customItem.price_czk || 0,
                price_usd: customItem.price_usd || 0,
                price_pln: customItem.price_pln || 0,
                price_chf: customItem.price_chf || 0,
                quote_image_url: customItem.quote_image_url || "",
                details: customItem.details || "",
                internal_product_manufacturer_id: customItem.internal_product_manufacturer_id || null,
                user_manufacturer_id: customItem.user_manufacturer_id || null,
                is_auto_exchange: customItem.is_auto_exchange ? 1 : 0,
                comment: customItem.comment || ""
            });

            setPrices({
                USD: customItem.price_usd,
                EUR: customItem.price_eur,
                GBP: customItem.price_gbp,
                HUF: customItem.price_huf,
                CZK: customItem.price_czk,
                PLN: customItem.price_pln,
                CHF: customItem.price_chf
            });

            setIcon({
                id: customItem.icon_id || 0,
                icon_svg: p.icon_svg || "",
                color: p.icon_color || "#000000",
            });

            setIconColor(p.icon_color || "#000000");

            if (customItem.internal_product_manufacturer_id != null) {
                setPreSelectedType("internal");
            } else {
                setPreSelectedType("user");
            }

            setPreSelectedId(customItem.internal_product_manufacturer_id | customItem.user_manufacturer_id);
        } else {
            setFormData({
                product_name: "",
                article_number: "",
                icon_id: 0,
                icon_color: "#000000",
                price_huf: 0,
                price_eur: 0,
                price_gbp: 0,
                price_czk: 0,
                price_usd: 0,
                price_pln: 0,
                price_chf: 0,
                quote_image_url: "",
                details: "",
                internal_product_manufacturer_id: null,
                user_manufacturer_id: null,
                is_auto_exchange: 1,
                comment: ""
            });

            setPrices({
                USD: 0,
                EUR: 0,
                GBP: 0,
                HUF: 0,
                CZK: 0,
                PLN: 0,
                CHF: 0
            });

            setIconColor("#000000");

            setSelectedManufacturer(null);
            setPreSelectedId(null);
            setPreSelectedType(null);
        }
    }, [existingProduct, productId, preSelectedType]);

    return (
        <>
        <BaseDialog
            onClose={() => handleClose()}
            title={hasProduct
                ? ts("edit_custom_item_title", "Edit custom item")
                : ts("add_custom_item_title", "Add custom item")
            }
            {...(hasProduct ? {
                actionsLeft: (
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: theme.palette.error.main,
                            color: '#fff',
                        }}
                        onClick={() => {
                            setSelectedProductId(productId);
                            setSelectedProductName(formData.product_name);
                            setIsDeleteOpen(true)
                        }}
                    >
                        {ts("p_tool_delete", "Delete")}
                    </Button>
                ),
                actions: (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => handleClose()}
                        >
                            {ts("plt_cancel", "Cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: grey[500],
                                color: '#fff',
                            }}
                            onClick={handleClone}
                        >
                            {ts("clone_custom_product", "Clone")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                        >
                            {ts("save_custom_product", "Save product")}
                        </Button>
                    </Box>
                ),
            } : {
                cancelLabel: ts("plt_cancel", "Cancel"),
                actionLabel: ts("add_custom_product", "Add product"),
                onAction: handleSubmit,
            })}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                }}
            >
                <IconPicker value={icon} onChange={setIcon} />

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                flex: 1,
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            {icon && (
                                <IconWithLiveColor icon={icon} iconColor={iconColor} setIconColor={setIconColor}/>
                            )}
                        </Box>

                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 2,
                            marginTop: 1
                        }}
                    >
                        <TextField
                            id="outlined"
                            required
                            label={ts("custom_item_name", "Name")}
                            size="small"
                            value={formData.product_name}
                            onChange={(e) => updateFormData("product_name", e.target.value)}
                            sx={{
                                flex: 1
                            }}
                        />

                        <Box sx={{ flex: 1 }}>
                            <ManufacturerAutocomplete setSelectedManufacturer={setSelectedManufacturer} selectedManufacturer={selectedManufacturer} preSelectedManufacturerId={preSelectedId} preSelectedManufacturerType={preSelectedType}/>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 2,
                            marginTop: 1
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    width: "100%",
                                }}
                            >
                                <TextField
                                    id="outlined"
                                    label={ts("custom_item_price", "Price")}
                                    size="small"
                                    required
                                    value={prices["USD"]}
                                    onChange={(e) => handlePriceChange("USD", Number(e.target.value))}
                                    sx={{
                                        flexGrow: 1,
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                    }}
                                    slotProps={{
                                        input: {
                                            endAdornment: <InputAdornment position="end">
                                                <Typography variant="caption" color="textSecondary">
                                                    USD
                                                </Typography>
                                            </InputAdornment>,

                                        }
                                    }}
                                />
                                { formData.is_auto_exchange === 1 && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            minWidth: 40,
                                            borderLeft: "none",
                                            borderRadius: "0 4px 4px 0",
                                        }}
                                        onClick={togglePrice}
                                    >
                                        { priceOpen ? (
                                            <ChevronUpIcon />
                                        ) : (
                                            <ChevronDownIcon />
                                        ) }
                                    </Button>
                                )}
                            </Box>

                            <TextField
                                id="outlined"
                                label={ts("custom_item_article_number", "Article number")}
                                size="small"
                                value={formData.article_number}
                                onChange={(e) => updateFormData("article_number", e.target.value)}
                                sx={{ width: "100%" }}
                            />
                        </Box>
                    </Box>

                    { priceOpen && (
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                marginTop: 2,
                            }}
                        >
                            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                                {["EUR", "CZK", "GBP", "HUF", "PLN", "CHF"].filter((c) => c !== "USD").map((currency) => {
                                    return (
                                        <Box key={currency} sx={{ position: "relative" }}>
                                            {formData.is_auto_exchange === 1 && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        marginBottom: 0.5,
                                                        marginTop: -1.5
                                                    }}
                                                >
                                                    <ArrowLeftRightIcon fontSize="small"/>
                                                </Box>
                                            )}

                                            <TextField
                                                label="Price"
                                                size="small"
                                                value={prices[currency]}
                                                onChange={(e) => handlePriceChange(currency, Number(e.target.value))}
                                                fullWidth
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {currency}
                                                                </Typography>
                                                            </InputAdornment>
                                                        )
                                                    }
                                                }}
                                            />
                                        </Box>
                                    )
                                }
                            )}
                            </Box>


                            <Box sx={{ flex: 1 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        marginBottom: 1,
                                    }}
                                >
                                    <Stack direction='row' spacing={1} alignItems='center'>
                                        <Typography variant="body2">Automatic exchange</Typography>
                                        <Switch
                                            checked={formData.is_auto_exchange === 1}
                                            onChange={() =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    is_auto_exchange: prev.is_auto_exchange === 1 ? 0 : 1
                                                }))
                                            }
                                            size="small"
                                        />
                                    </Stack>

                                    <Tooltip title={ts("tooltip_excange_information", "Automatically calculates other currencies as you type. If you switch this off then only the edited price will be changed, others will not be affected.")}>
                                        <IconButton size="small">
                                            <InformationIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <TextField
                        id="outlined-multiline-static"
                        label={ts("custom_item_comment", "Comment")}
                        value={formData.comment}
                        onChange={(e) => updateFormData("comment", e.target.value)}
                        multiline
                        rows={4}
                        sx={{
                            width: '100%',
                            marginTop: 1
                        }}
                    />

                    <Tooltip title={ts("tooltip_quote_image", "Paste here the url of the product image ending with .png or .jpg. It will appear on the project's quote tab after pressing.")}>
                        <TextField
                            id="outlined"
                            label={ts("custom_item_image_url", "Quote image URL (PNG/JPG)")}
                            size="small"
                            value={formData.quote_image_url}
                            onChange={(e) => updateFormData("quote_image_url", e.target.value)}
                            sx={{
                                width: '100%',
                                marginTop: 1
                            }}
                        />
                    </Tooltip>

                    <Tooltip title={ts("tooltip_quote_details", "Paste here the url of the product details ending with .png .jpg or .pdf. It will appear on the project's quote tab after pressing.")}>
                        <TextField
                            id="outlined"
                            label={ts("custom_item_details_url", "Quote details URL (PNG/JPG/PDF)")}
                            size="small"
                            value={formData.details}
                            onChange={(e) => updateFormData("details", e.target.value)}
                            sx={{
                                width: '100%',
                                marginTop: 1
                            }}
                        />
                    </Tooltip>
        </BaseDialog>

        {isDeleteOpen && (
            <DeleteUserCustomItemDialog
                onClose={() => setIsDeleteOpen(false)}
                productId={selectedProductId}
                productName={selectedProductName}
            />
        )}
        </>
    );
};
